import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, FileText, MapPin } from "lucide-react";
import { SendLetterCard, FullPettyCard } from "../components/PricingSection";
import { Marquee } from "../components/Marquee";
import { CtaBand } from "../components/CtaBand";
import { scrollToSection } from "../components/Navbar";

const assurances = [
  { icon: ShieldCheck, text: "Pay only when ready to send" },
  { icon: FileText, text: "Court-ready documents, every time" },
  { icon: MapPin, text: "Coverage across all 50 states" },
];

export default function PricingPage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => scrollToSection(location.hash), 300);
      return () => clearTimeout(t);
    }
  }, [location]);
  return (
    <main className="pt-[68px]">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="h-[3px] w-14 bg-[#2563EB]" />
          <span className="text-xs font-bold tracking-[0.35em] uppercase text-[#71717a]">
            Pricing
          </span>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#111827] max-w-3xl"
        >
          Pick your level of <span className="highlight-swash">service.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-5 text-base sm:text-lg text-[#52525B] max-w-xl leading-relaxed"
        >
          One-time flat fees. No subscriptions, no hourly billing, no lawyer
          required. You only pay when your letter is ready to send.
        </motion.p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 max-w-4xl lg:max-w-none mx-auto">
          <SendLetterCard />
          <FullPettyCard />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-14 grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto"
        >
          {assurances.map((a) => (
            <div
              key={a.text}
              className="flex items-center gap-3 bg-white border border-[#E5E7EB] rounded-2xl px-5 py-4"
            >
              <a.icon size={20} className="text-[#2563EB] shrink-0" />
              <span className="text-sm font-semibold text-[#3f3f46]">
                {a.text}
              </span>
            </div>
          ))}
        </motion.div>
      </section>

      <Marquee />
      <CtaBand />
    </main>
  );
}
